import ImagePreview from './ImagePreview';
import FinalImage from './FinalImage';
import ImagePlaceholder from './ImagePlaceholder';

interface Message {
  imageId?: string;
  image?: string;
  previewImage?: string;
  isGeneratingImage?: boolean;
  imageGenerationStartTime?: number;
}

interface ImageContainerProps {
  message: Message;
  sessionTitle?: string;
  onImageClick: () => void;
}

type ImageState = 'placeholder' | 'preview' | 'final';

export default function ImageContainer({ 
  message, 
  sessionTitle, 
  onImageClick 
}: ImageContainerProps) {
  const { imageId, image, previewImage, isGeneratingImage, imageGenerationStartTime } = message;

  // Determine which image state to display
  const getImageState = (): ImageState => {
    // Final image takes priority
    if (imageId || image) {
      return 'final';
    }
    
    // Preview image when generating
    if (previewImage) {
      return 'preview';
    }
    
    // Placeholder when generating but no content yet
    if (isGeneratingImage) {
      return 'placeholder';
    }
    
    // Default fallback (though this shouldn't happen)
    return 'placeholder';
  };

  const imageState = getImageState();

  const renderImageByState = () => {
    switch (imageState) {
      case 'placeholder':
        return <ImagePlaceholder />;
      
      case 'preview':
      case 'final':
        return (
          <ImagePreview 
            previewImage={previewImage}
            imageId={imageId}
            isGenerating={isGeneratingImage}
            imageGenerationStartTime={imageGenerationStartTime}
            onImageClick={onImageClick}
          />
        );
      

      default:
        return <ImagePlaceholder />;
    }
  };

  return (
    <div className="relative border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden hover:border-gray-300 dark:hover:border-gray-600 transition-colors bg-white dark:bg-gray-800 hover:bg-gray-50/50 dark:hover:bg-gray-700/50 w-full aspect-square">
      <div className="absolute inset-0 flex items-center justify-center">
        {renderImageByState()}
      </div>
    </div>
  );
}